/**
 * Descriptor → generic engine adapter (#997 — Story 3.1).
 *
 * Maps a `SectionDescriptor` to the props the existing generic engine
 * (`RepeatableInstancesSection` + `useRepeatableInstanceOrchestrator`) consumes
 * — labels, table config, instance↔field mapping, shared fields, attachments,
 * Additional Information, and the per-unit edit body — and renders it. This is
 * the proof that a section defined purely from a descriptor drives the engine at
 * Racks capability parity WITH NO hand-authored per-section edit body.
 *
 * It builds the orchestrator callbacks generically from the descriptor field
 * set; nothing here is section-specific. No live section is touched.
 */

import { useQueryClient } from '@tanstack/react-query';
import React, { useCallback, useMemo } from 'react';

import {
  DescriptorEditFields,
  fromYesNoValue,
  toYesNoValue,
  type DescriptorFieldValues,
} from './descriptorEditFields';
import { DescriptorInstanceEditFormBody } from './DescriptorInstanceEditFormBody';
import {
  descriptorPerUnitFields,
  type SectionDescriptor,
} from './sectionDescriptor.types';
import { parseOptionalNumber } from '../../components/repeatable/repeatableSectionListUtils';
import {
  INSTANCE_UNIT_FIELDS_BY_SECTION,
  listCellWithSavedUnit,
} from '../dimensionUnitChoices';
import {
  useCopyInstanceDimensionUnits,
  usePinInstanceDimensionUnits,
  useDimensionUnits,
  useRegisterListedInstanceDimensionUnits,
} from '../dimensionUnits';
import {
  RepeatableInstancesSection,
  type RepeatableInstancesSectionLabels,
  type RepeatableInstancesSectionTableConfig,
} from '../../components/repeatableSections/RepeatableInstancesSection';
import { offlineRequestsKeys } from '../../hooks/queries/useOfflineRequestsQuery';
import {
  descriptorSupportsDelegatedFlush,
  resolveDelegatedFlushBinding,
} from '../../services/descriptorEditSessionFlush';
import { moveSelectedFilesFromBaseToUnitScope } from '../../utils/repeatableInstanceAttachmentSections';

import type { SelectedFile } from '../../components/forms/FileUpload';
import type { RepeatableInstanceEditFormRenderProps } from '../../components/repeatableSections/repeatableInstanceEditFormTypes';
import type { RepeatableInstanceSaveHandle } from '../../components/repeatableSections/repeatableInstanceSaveHandle';
import type {
  RepeatableInstance,
  RepeatableInstanceEditSessionConfig,
  RepeatableTableLayout,
} from '../../components/repeatableSections/types';
import type { SavedAttachment } from '../../services/attachmentService';
import type { AppRole } from '../../types/auth';
import type { DeviceStepId } from '../../utils/deviceRowMapper';
import type { SubsectionFieldDescriptor } from '../../utils/subsectionHeaderMetrics';

const DEVICE_STEP_IDS = new Set<string>([
  'rack-power',
  'small-ups',
  'large-ups',
  'power-distribution',
  'cooling-readiness',
  'liquid-cooling',
]);

export function descriptorDeviceStepId(
  descriptor: Pick<SectionDescriptor, 'stepId'>,
): DeviceStepId | undefined {
  return DEVICE_STEP_IDS.has(descriptor.stepId)
    ? (descriptor.stepId as DeviceStepId)
    : undefined;
}

/** A generic descriptor-defined instance row. */
export interface DescriptorInstance extends RepeatableInstance {
  [dataField: string]: string | number | boolean | null | undefined;
}

export type DescriptorInstancePatch = Record<
  string,
  string | number | boolean | null | undefined
> & { displayOrder?: number };

/** Editor metric fields (list header + summary short labels) from the descriptor. */
export function descriptorMetricFields(
  descriptor: SectionDescriptor,
): SubsectionFieldDescriptor[] {
  return descriptorPerUnitFields(descriptor)
    .slice(0, 4)
    .map(field => ({ key: field.dataField, shortLabel: field.shortLabel }));
}

/**
 * List table config for the engine. When the descriptor declares a
 * `listColumns` projection (#997 Story 6.3 — every section's list columns are
 * product-approved and hand-authored), the engine renders it; otherwise it
 * falls back to the generic first-3-per-unit-fields preview table
 * (synthetic descriptors, pre-migration peers).
 */
export function descriptorTableConfig(
  descriptor: SectionDescriptor,
): RepeatableInstancesSectionTableConfig<DescriptorInstance> {
  const getEditChromeTitle = (
    instanceName: string | undefined,
    { isNew, displayOrder }: { isNew?: boolean; displayOrder?: number },
  ): string =>
    // A cleared name is `''` (not nullish), so `??` would keep the blank and
    // render an empty chrome title — fall through to the entity + order label
    // for any blank/whitespace-only name. An unnamed NEW unit takes the
    // section's new-record title (bespoke RackEditChrome: 'New rack')
    // (#997 Story 6.3 R5).
    instanceName && instanceName.trim()
      ? instanceName
      : isNew
      ? descriptor.labels.newInstanceChromeTitle ??
        `New ${descriptor.labels.entitySingular.toLowerCase()}`
      : `${descriptor.labels.entitySingular} ${displayOrder ?? ''}`.trim();

  const projection = descriptor.listColumns;
  if (projection) {
    return {
      locationTagCellColumn: projection.locationTagCellColumn,
      locationTagSublineColumn: projection.locationTagSublineColumn,
      columnsForLayout: projection.columnsForLayout,
      columnAlign: projection.columnAlign,
      headerLabel: projection.headerLabel,
      formatCell: (instance, column, layout) =>
        projection.formatCell(instance, column, layout),
      getColumnSubline: projection.getColumnSubline
        ? (instance, column, layout) =>
            projection.getColumnSubline!(instance, column, layout)
        : undefined,
      columnUsesDoubleLine: projection.columnUsesDoubleLine,
      getEditChromeTitle,
    };
  }

  const columnFields = descriptorPerUnitFields(descriptor).slice(0, 3);
  const columns = columnFields.map(field => field.dataField);
  const labelByColumn = new Map(
    columnFields.map(field => [field.dataField, field.shortLabel]),
  );

  return {
    columnsForLayout: () => columns,
    headerLabel: column => labelByColumn.get(column) ?? column,
    formatCell: (instance, column) => {
      const value = instance[column];
      if (value === undefined || value === null) return '';
      return typeof value === 'boolean'
        ? value
          ? 'Yes'
          : 'No'
        : String(value);
    },
    getEditChromeTitle,
  };
}

/**
 * Engine labels derived from the descriptor's label block. Each templated
 * label yields to a per-descriptor override when declared (#997 Story 6.3 R5 /
 * GATE-5 — Racks binds its hand-authored `rackConfigurationsSubsectionCopy`);
 * descriptors without overrides keep the templates unchanged.
 */
export function descriptorEngineLabels(
  descriptor: SectionDescriptor,
): RepeatableInstancesSectionLabels {
  const { labels } = descriptor;
  return {
    entityPrefix: labels.entitySingular,
    entityLabelPlural: labels.entityPlural,
    addButtonLabel: labels.addButtonLabel,
    emptyStateTitle: labels.emptyStateTitle,
    editSaveLabel: labels.editSaveLabel ?? `Save ${labels.entitySingular}`,
    editSaveDraftLabel:
      labels.editSaveDraftLabel ?? `Save ${labels.entitySingular} draft`,
    editDeleteLabel:
      labels.editDeleteLabel ?? `Delete ${labels.entitySingular}`,
    deleteConfirmTitle:
      labels.deleteConfirmTitle ?? `Delete ${labels.entitySingular}?`,
    deleteConfirmMessage:
      labels.deleteConfirmMessage ??
      `This ${labels.entitySingular.toLowerCase()} will be removed.`,
    editAllListLabel: labels.editAllListLabel ?? `All ${labels.entityPlural}`,
    editFormTitle: labels.editFormTitle,
    editFormSubtitle: labels.editFormSubtitle,
    editFormMetricFields: descriptorMetricFields(descriptor),
    editDiscardTitle: labels.editDiscardTitle,
    editDiscardMessage: labels.editDiscardMessage,
    editDiscardConfirmLabel: labels.editDiscardConfirmLabel,
    editDiscardKeepEditingLabel: labels.editDiscardKeepEditingLabel,
  };
}

/** editor field-values → instance patch (per-unit fields + identity). */
export function descriptorFieldMapToPatch(
  descriptor: SectionDescriptor,
): (fields: DescriptorFieldValues) => DescriptorInstancePatch {
  const fields = descriptorPerUnitFields(descriptor);
  const knownKeys = new Set<string>([
    'instanceName',
    'instanceStatus',
    ...fields.map(field => field.dataField),
  ]);
  return values => {
    // An editor key outside the descriptor field set + identity keys is a body
    // wiring bug (e.g. writing rack-shaped `rackName` instead of `instanceName`)
    // — its value would vanish on save. Fail loudly, never drop silently
    // (#997 Story 6.3 GATE-4).
    const unmapped = Object.keys(values).filter(
      key => !knownKeys.has(key) && values[key] !== undefined,
    );
    if (unmapped.length > 0) {
      throw new Error(
        `[descriptorFieldMapToPatch] Unmapped editor field(s) for descriptor "${
          descriptor.id
        }": ${unmapped.join(
          ', ',
        )} — the edited value(s) would be dropped on save.`,
      );
    }
    const patch: DescriptorInstancePatch = {};
    if (typeof values.instanceName === 'string') {
      patch.instanceName = values.instanceName;
    }
    if (typeof values.instanceStatus === 'string') {
      patch.instanceStatus = values.instanceStatus;
    }
    for (const field of fields) {
      const raw = values[field.dataField];
      if (field.type === 'yesNo') {
        // A yesNo field persists as a boolean (BIT), not the editor's 'Yes'/'No'.
        patch[field.dataField] = fromYesNoValue(raw);
      } else if (field.type === 'number') {
        // The editor holds numbers as raw text; coerce to `number` at this
        // boundary (mirrors bespoke `RackEditFormContent` `parseOptionalNumber`,
        // #997 Story 6.3) so the persisted session and change-detector are
        // number-typed, not `"5"` vs a refetched `5`.
        patch[field.dataField] = parseOptionalNumber(
          raw == null ? '' : String(raw),
        );
      } else if (field.type === 'locationTag') {
        if (raw === undefined) {
          patch[field.dataField] = undefined;
        } else if (raw === null || raw === '') {
          patch[field.dataField] = null;
        } else {
          const isCanonicalInteger =
            typeof raw === 'number' ||
            (typeof raw === 'string' && /^-?[1-9]\d*$/.test(raw));
          const numericId = isCanonicalInteger ? Number(raw) : Number.NaN;
          patch[field.dataField] =
            Number.isSafeInteger(numericId) && numericId !== 0
              ? numericId
              : null;
        }
      } else {
        patch[field.dataField] = raw;
      }
    }
    return patch;
  };
}

/**
 * Resolve the display list: local session instances, else the persisted array.
 *
 * Persisted-shape bridge (#997 Story 6.3 R3 / GATE-1a; Story 6.4 §2): a
 * flush-delegating section's PERSISTED shape (Racks `rackName`/`rackStatus`,
 * Small UPS `name`/`status`) differs from the engine instance shape
 * (`instanceName`/`instanceStatus`). A raw persisted row seeding the session
 * would carry NO engine identity — a later flush would PUT a blank name. Such
 * descriptors therefore convert the persisted rows in via their delegated-flush
 * binding; peer sections (whose persisted rows already ARE
 * `RepeatableInstance`-shaped) pass through raw.
 */
export function descriptorResolveForDisplay(
  descriptor: SectionDescriptor,
): (
  instances: DescriptorInstance[] | undefined,
  legacySource: Record<string, unknown>,
) => DescriptorInstance[] {
  const key = descriptor.offlineBinding.persistedInstancesKey;
  const binding = resolveDelegatedFlushBinding(descriptor);
  // Shape-aware: the orchestrator passes `legacyRequestFields[key]` as BOTH the
  // first arg and the fallback source, so either path can hand us persisted
  // (section-shaped) rows. Convert only rows missing engine identity —
  // already-converted instances pass through untouched (conversion is not
  // idempotent: re-running it would blank instanceName/instanceStatus).
  const toEngineShape = (item: DescriptorInstance): DescriptorInstance =>
    binding &&
    (item as { instanceStatus?: unknown }).instanceStatus === undefined
      ? (binding.toEngineInstance(item) as DescriptorInstance)
      : item;
  return (instances, legacySource) => {
    const persisted = legacySource[key];
    const chosen =
      instances && instances.length > 0
        ? instances
        : Array.isArray(persisted)
        ? (persisted as DescriptorInstance[])
        : [];
    return binding ? chosen.map(toEngineShape) : chosen;
  };
}

/** Build a new draft unit from a patch. */
export function descriptorCreateDraft(
  data: DescriptorInstancePatch,
  displayOrder: number,
  tempId: number,
): DescriptorInstance {
  return { id: tempId, instanceStatus: 'new', displayOrder, ...data };
}

/** Apply a patch onto an existing unit. */
export function descriptorApplyPatch(
  instance: DescriptorInstance,
  patch: DescriptorInstancePatch,
): DescriptorInstance {
  return { ...instance, ...patch };
}

function uniqueName(base: string, taken: Iterable<string | undefined>): string {
  const seen = new Set(taken);
  let n = 1;
  let candidate = `${base} ${n}`;
  while (seen.has(candidate)) {
    n += 1;
    candidate = `${base} ${n}`;
  }
  return candidate;
}

/** Ensure a create carries an instance name (auto-numbered when blank). */
export function descriptorEnsureNameForCreate(
  descriptor: SectionDescriptor,
): (
  data: DescriptorInstancePatch,
  tempId: number,
  takenNames: Iterable<string | undefined>,
) => DescriptorInstancePatch {
  return (data, _tempId, takenNames) => {
    if (typeof data.instanceName === 'string' && data.instanceName.trim()) {
      return data;
    }
    return {
      ...data,
      instanceName: uniqueName(descriptor.labels.entitySingular, takenNames),
    };
  };
}

/** Clone an existing unit for duplication, with a unique "(copy N)" name. */
export function descriptorCloneForCreate(
  descriptor: SectionDescriptor,
): (
  source: DescriptorInstance,
  takenNames: Iterable<string | undefined>,
  tempId: number,
) => Partial<DescriptorInstance> {
  return (source, takenNames, tempId) => {
    const base = `${
      source.instanceName ?? descriptor.labels.entitySingular
    } (copy`;
    const seen = new Set(takenNames);
    let n = 1;
    let candidate = `${base} ${n})`;
    while (seen.has(candidate)) {
      n += 1;
      candidate = `${base} ${n})`;
    }
    return { ...source, id: tempId, instanceName: candidate };
  };
}

/** instance → editor field-value map (per-unit fields + identity). */
export function descriptorInstanceToFieldMap(
  descriptor: SectionDescriptor,
): (instance: DescriptorInstance) => DescriptorFieldValues {
  const fields = descriptorPerUnitFields(descriptor);
  return instance => {
    const values: DescriptorFieldValues = {
      instanceName: instance.instanceName,
      instanceStatus: instance.instanceStatus,
    };
    for (const field of fields) {
      const raw = instance[field.dataField];
      if (field.type === 'yesNo') {
        // Render a stored boolean (BIT) as the selector's 'Yes'/'No' editor form.
        values[field.dataField] = toYesNoValue(
          raw as string | boolean | undefined,
        );
      } else if (field.type === 'locationTag') {
        values[field.dataField] = typeof raw === 'number' ? raw : null;
      } else {
        values[field.dataField] =
          typeof raw === 'number'
            ? String(raw)
            : (raw as string | boolean | undefined);
      }
    }
    return values;
  };
}

export interface DescriptorEngineSectionProps {
  descriptor: SectionDescriptor;
  requestId?: string;
  isEditing?: boolean;
  disabled?: boolean;
  requestStage?: string;
  /** User roles + view-mode for the DECOUPLED visibility path. */
  userRoles?: readonly AppRole[];
  showFullDetails?: boolean;
  userEmail?: string;
  draftInstances?: DescriptorInstance[];
  onDraftInstancesChange?: (instances: DescriptorInstance[]) => void;
  legacyRequestFields?: Record<string, unknown>;
  /**
   * Persisted (Request Detail) edit-session bridge (#997 Story 6.2/6.3). When
   * supplied, the live instance list is written back into `editedData` under the
   * descriptor's `persistedInstancesKey` on every session change, so the session
   * survives step navigation and participates in request-save dirty-tracking —
   * the generic equivalent of the peer sections' `useRepeatableInstanceRequestDetailSession`
   * (`toEditedDataPatch` is just `{ [persistedInstancesKey]: instances }`).
   */
  applyEditedDataPatch?: (patch: Record<string, unknown>) => void;
  /** Per-unit attachment wiring (forwarded to the engine + edit-form body). */
  attachmentsBySection?: Record<string, SavedAttachment[]>;
  onAttachFiles?: (sectionId: string) => void;
  onDeleteAttachment?: (attachmentId: number) => void;
  selectedFiles?: Record<string, SelectedFile[]>;
  onFilesSelected?: (sectionId: string, files: SelectedFile[]) => void;
  offlineMode?: boolean;
  onCaptionChange?: (attachmentId: number, caption: string) => void;
  pendingCaptionEdits?: Record<number, string>;
  /**
   * Optional per-section edit body override (#997 Story 6.3 R6). When supplied,
   * the engine mounts this instead of the generic `DescriptorInstanceEditFormBody`
   * — the seam Racks uses to render its bespoke `RackEditFormContent`
   * (`RacksInstanceEditFormBody`) at byte parity. Peers omit it.
   */
  editBody?: (props: RepeatableInstanceEditFormRenderProps) => React.ReactNode;
  /**
   * Optional draft/name/clone factory overrides (#997 Story 6.3 R7). Racks binds
   * its proven bespoke utils (`racksEngineBindings`) so create-flow list
   * semantics (draft defaults, serial-hash auto-names, duplicate naming) stay
   * byte-identical; descriptors without overrides keep the generic factories.
   */
  createDraft?: (
    data: DescriptorInstancePatch,
    displayOrder: number,
    tempId: number,
  ) => DescriptorInstance;
  ensureNameForCreate?: (
    data: DescriptorInstancePatch,
    tempId: number,
    takenNames: Iterable<string | undefined>,
  ) => DescriptorInstancePatch;
  cloneForCreate?: (
    source: DescriptorInstance,
    takenNames: Iterable<string | undefined>,
    tempId: number,
  ) => Partial<DescriptorInstance>;
  /** Shared/request-level field updates (unused for Racks — `sharedFieldKeys: []`). */
  onSharedFieldChange?: (field: string, value: string | boolean) => void;
  registerInstanceSaveHandle?: (
    handle: RepeatableInstanceSaveHandle | null,
  ) => void;
  onInstanceEditorActiveChange?: (
    active: boolean,
    footerSaveLabel?: string,
  ) => void;
  testID?: string;
}

/**
 * Render a descriptor-defined repeatable section through the generic engine.
 */
export function DescriptorEngineSection({
  descriptor,
  requestId,
  isEditing = false,
  disabled = false,
  requestStage = 'DRAFT',
  userRoles = [],
  showFullDetails = true,
  userEmail,
  draftInstances = [],
  onDraftInstancesChange,
  legacyRequestFields = {},
  applyEditedDataPatch,
  attachmentsBySection = {},
  onAttachFiles,
  onDeleteAttachment,
  selectedFiles = {},
  onFilesSelected,
  offlineMode = false,
  onCaptionChange,
  pendingCaptionEdits,
  editBody,
  createDraft: createDraftOverride,
  ensureNameForCreate: ensureNameForCreateOverride,
  cloneForCreate: cloneForCreateOverride,
  onSharedFieldChange,
  registerInstanceSaveHandle,
  onInstanceEditorActiveChange,
  testID = `descriptor-engine-${descriptor.id}`,
}: DescriptorEngineSectionProps): React.ReactElement {
  const labels = useMemo(
    () => descriptorEngineLabels(descriptor),
    [descriptor],
  );
  const dimensionUnits = useDimensionUnits();
  const copyInstanceUnits = useCopyInstanceDimensionUnits();
  const pinInstanceUnits = usePinInstanceDimensionUnits();
  const registerListedUnits = useRegisterListedInstanceDimensionUnits();
  const instanceUnitFields = INSTANCE_UNIT_FIELDS_BY_SECTION[descriptor.id];
  const onListedInstances = useCallback(
    (instanceIds: readonly number[]) => {
      if (!instanceUnitFields) return;
      registerListedUnits(descriptor.id, instanceIds, instanceUnitFields);
    },
    [descriptor.id, instanceUnitFields, registerListedUnits],
  );
  const onPersistInstance = useCallback(
    (instanceId: number) => {
      if (!instanceUnitFields) return;
      pinInstanceUnits(instanceId, instanceUnitFields);
    },
    [pinInstanceUnits, instanceUnitFields],
  );
  const onDuplicateInstance = useCallback(
    (source: DescriptorInstance, destTempId: number) => {
      if (!instanceUnitFields || typeof source.id !== 'number') return;
      copyInstanceUnits(source.id, destTempId, instanceUnitFields);
    },
    [copyInstanceUnits, instanceUnitFields],
  );
  const table = useMemo(() => {
    const base = descriptorTableConfig(descriptor);
    return {
      ...base,
      formatCell: (
        instance: DescriptorInstance,
        column: string,
        layout: RepeatableTableLayout,
      ) =>
        listCellWithSavedUnit(
          descriptor.id,
          column,
          base.formatCell(instance, column, layout),
          dimensionUnits,
          typeof instance.id === 'number' ? instance.id : undefined,
        ),
    };
  }, [descriptor, dimensionUnits]);

  const instanceToFieldMap = useMemo(
    () => descriptorInstanceToFieldMap(descriptor),
    [descriptor],
  );
  const fieldMapToPatch = useMemo(
    () => descriptorFieldMapToPatch(descriptor),
    [descriptor],
  );
  const resolveForDisplay = useMemo(
    () => descriptorResolveForDisplay(descriptor),
    [descriptor],
  );
  const ensureNameForCreate = useMemo(
    () =>
      ensureNameForCreateOverride ?? descriptorEnsureNameForCreate(descriptor),
    [descriptor, ensureNameForCreateOverride],
  );
  const cloneForCreate = useMemo(
    () => cloneForCreateOverride ?? descriptorCloneForCreate(descriptor),
    [descriptor, cloneForCreateOverride],
  );
  const createDraft = useMemo(
    () => createDraftOverride ?? descriptorCreateDraft,
    [createDraftOverride],
  );
  const applyPatch = useCallback(descriptorApplyPatch, []);

  const queryClient = useQueryClient();

  // Save/flush/remap wiring (#997 Story 6.2/6.4). Only descriptors with a proven
  // delegating flush binding get an edit session + create-time file move; every
  // other descriptor keeps the pre-6.2 behavior (offline-cache fallback, no move).
  const supportsFlush = descriptorSupportsDelegatedFlush(descriptor);

  const editSession = useMemo<
    RepeatableInstanceEditSessionConfig<DescriptorInstance> | undefined
  >(() => {
    const binding = resolveDelegatedFlushBinding(descriptor);
    if (!supportsFlush || !binding) {
      return undefined;
    }
    return {
      flushSession: async input => {
        // GATE-3 (#997 Story 6.3 R4): an offline-created request ('o-…') must
        // take the offline queue even on a connected device — the engine's
        // `offlineMode` is NETWORK state only. OR in the request-shape flag
        // (mirrors `useRepeatableInstanceRequestDetailSession` / bespoke
        // `RackPower.tsx` `isOfflineRequest`).
        const isOfflineRequest = input.requestId.startsWith('o-');
        const flushed = await binding.flush(
          { ...input, offlineMode: input.offlineMode || isOfflineRequest },
          descriptor,
        );
        // Mirror the bespoke wrapper's post-flush invalidation exactly: refetch
        // the section's ROW queries so the list/editor read the saved rows with
        // real ids (#997 Story 6.4 — per-descriptor, so NO rack query keys fire
        // for Small UPS). Note this does NOT touch the attachments query feeding
        // `attachmentsBySection` — neither did bespoke; the remapped attachments
        // surface via the host's own attachments query lifecycle.
        for (const queryKey of binding.listQueryKeys(input.requestId)) {
          await queryClient.invalidateQueries({ queryKey });
        }
        // Refresh the request payload's embedded instances too — view lists fall
        // back to that embed when the row query is empty, so a stale embed
        // resurrects deleted units as ghosts (#997 Story 6.3).
        await queryClient.invalidateQueries({
          queryKey: offlineRequestsKeys.detail(input.requestId),
        });
        return flushed as DescriptorInstance[];
      },
    };
  }, [supportsFlush, descriptor, queryClient]);

  // Persisted-session → editedData bridge (#997 Story 6.2/6.3/6.4). Mirrors
  // `useRepeatableInstanceRequestDetailSession.onSessionListChange`: when editing a
  // saved request, write the live list into editedData under the descriptor's
  // persisted-instances key so it survives navigation + drives request-save.
  // Persisted-shape bridge (Story 6.3 R3 / GATE-1b): every consumer of
  // `editedData[persistedInstancesKey]` (request-save, Summary, PDF merge)
  // expects the SECTION shape (Racks `RackConfiguration`, Small UPS
  // `SmallUpsConfiguration`), so flush-delegating descriptors convert the engine
  // instances OUT via their binding before patching; peers patch raw.
  const onSessionListChange = useMemo<
    ((instances: DescriptorInstance[]) => void) | undefined
  >(() => {
    if (!isEditing || !applyEditedDataPatch) {
      return undefined;
    }
    const key = descriptor.offlineBinding.persistedInstancesKey;
    const binding = resolveDelegatedFlushBinding(descriptor);
    if (supportsFlush && binding) {
      const requestIdNum = requestId ? Number.parseInt(requestId, 10) || 0 : 0;
      return (instances: DescriptorInstance[]) =>
        applyEditedDataPatch({
          [key]: instances.map(instance =>
            binding.toPersistedInstance(instance, requestIdNum, descriptor),
          ),
        });
    }
    return (instances: DescriptorInstance[]) =>
      applyEditedDataPatch({ [key]: instances });
  }, [isEditing, applyEditedDataPatch, descriptor, supportsFlush, requestId]);

  const onCreateInstance = useMemo<
    ((tempId: number) => void) | undefined
  >(() => {
    if (!supportsFlush || !onFilesSelected) {
      return undefined;
    }
    const baseSectionIds = descriptor.attachmentBaseSectionIds;
    if (!baseSectionIds?.length) {
      return undefined;
    }
    return (tempId: number) =>
      moveSelectedFilesFromBaseToUnitScope(
        selectedFiles,
        baseSectionIds,
        tempId,
        onFilesSelected,
      );
  }, [supportsFlush, descriptor, selectedFiles, onFilesSelected]);

  const renderEditFields = useCallback(
    (props: {
      fields: DescriptorFieldValues;
      setField: (
        field: string,
        value: string | boolean | number | null,
      ) => void;
      disabled: boolean;
      requestStage: string;
      requestId?: string;
      fieldGroupKeys?: readonly string[];
    }): React.ReactNode => (
      <DescriptorEditFields
        descriptor={descriptor}
        values={props.fields}
        setField={props.setField}
        disabled={props.disabled}
        visibility={{
          viewMode: showFullDetails === false ? 'basic' : 'full',
          roles: userRoles,
          requestStage: props.requestStage,
          requestId: props.requestId,
          userEmail,
        }}
        fieldGroupKeys={props.fieldGroupKeys}
        requestId={props.requestId}
        isOnline={!offlineMode}
        testID={
          props.fieldGroupKeys
            ? `descriptor-edit-fields-${props.fieldGroupKeys.join('-')}`
            : undefined
        }
      />
    ),
    [descriptor, showFullDetails, userRoles, userEmail, offlineMode],
  );

  // Full multi-card edit body (per-unit fields + attachment cards + Additional
  // Information) only when the descriptor declares per-card attachment sections
  // (the live Racks path); otherwise the engine falls back to its single-card
  // shell (the synthetic descriptor path, which has no attachment base sections).
  const useFullEditBody =
    descriptor.attachmentsEnabled &&
    (descriptor.attachmentBaseSectionIds?.length ?? 0) > 0;
  const renderEditFormContent = useMemo(
    () =>
      editBody ??
      (useFullEditBody
        ? (props: RepeatableInstanceEditFormRenderProps): React.ReactNode => (
            <DescriptorInstanceEditFormBody
              descriptor={descriptor}
              {...props}
            />
          )
        : undefined),
    [descriptor, useFullEditBody, editBody],
  );

  return (
    <RepeatableInstancesSection<DescriptorInstance, DescriptorInstancePatch>
      labels={labels}
      table={table}
      sectionId={descriptor.id}
      devicesStepId={descriptorDeviceStepId(descriptor)}
      requestId={requestId}
      isEditing={isEditing}
      disabled={disabled}
      requestStage={requestStage}
      showFullDetails={showFullDetails}
      draftInstances={draftInstances}
      onDraftInstancesChange={onDraftInstancesChange}
      legacyRequestFields={legacyRequestFields}
      persistedInstancesKey={descriptor.offlineBinding.persistedInstancesKey}
      resolveForDisplay={resolveForDisplay}
      createDraft={createDraft}
      applyPatch={applyPatch}
      ensureNameForCreate={ensureNameForCreate}
      cloneForCreate={cloneForCreate}
      instanceToFieldMap={instanceToFieldMap}
      fieldMapToPatch={fieldMapToPatch}
      renderEditFields={renderEditFields}
      renderEditFormContent={renderEditFormContent}
      attachmentsBySection={attachmentsBySection}
      onAttachFiles={onAttachFiles}
      onDeleteAttachment={onDeleteAttachment}
      selectedFiles={selectedFiles}
      onFilesSelected={onFilesSelected}
      offlineMode={offlineMode}
      onCaptionChange={onCaptionChange}
      pendingCaptionEdits={pendingCaptionEdits}
      onSharedFieldChange={onSharedFieldChange}
      registerInstanceSaveHandle={registerInstanceSaveHandle}
      onInstanceEditorActiveChange={onInstanceEditorActiveChange}
      editSession={editSession}
      onCreateInstance={onCreateInstance}
      onPersistInstance={onPersistInstance}
      onDuplicateInstance={onDuplicateInstance}
      onListedInstances={onListedInstances}
      onSessionListChange={onSessionListChange}
      testID={testID}
    />
  );
}
